import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useIoContext, useOverlayContext } from '~/contexts';
import { useIntegration } from '~/hooks/useIntegration';
import { useLocaleKeys } from '~/i18n';
import { wait } from '~/infra/utils';
import { accountingIntegration } from '~/models/integration/selectors';
import { useStoreActions } from '~/store/store-context';
import { Accounting } from '~/types/api.model';
import { LottiePlayer } from '../../LottiePlayer/LottiePlayer';
import qboSyncAnimation from './qbo-sync.json';
import styles from './QboXeroIntegrationStep.module.scss';
import xeroSyncAnimation from './xero-sync.json';
const POLLING_INTERVAL = 2000;
const POLLING_TIMEOUT = 2 * 60 * 1000;
export const GetQboXeroIntegrationStepTID = (parentTestId)=>getTestIds({
        parentTestId
    });
const integrationConnectionStepTextsPath = 'vendorOnboarding.integrationConnectionStep';
export const QboXeroIntegrationStep = (param)=>{
    let { testId, accountingType, connected, onContinue, onConnectingChange, onConnectedChange, submitRef } = param;
    const testIds = useMemo(()=>GetQboXeroIntegrationStepTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys(integrationConnectionStepTextsPath);
    const { browser: { openNewTab } } = useIoContext();
    const { connect } = useIntegration(accountingType);
    const { fetchIntegrationsSummary } = useStoreActions();
    const { showErrorToast } = useOverlayContext();
    const [isWaiting, setIsWaiting] = useState(false);
    const pollingAborted = useRef(false);
    useEffect(()=>{
        return ()=>{
            pollingAborted.current = true;
        };
    }, []);
    const isQbo = accountingType === Accounting.QBO;
    const animationData = isQbo ? qboSyncAnimation : xeroSyncAnimation;
    const connectedMessage = isQbo ? t.connectedQbo() : t.connectedXero();
    const pollForReadyIntegration = useCallback(async (oauthTab)=>{
        const start = Date.now();
        while(!pollingAborted.current && Date.now() - start < POLLING_TIMEOUT){
            const summary = await fetchIntegrationsSummary();
            const integration = accountingIntegration(summary, accountingType);
            if (integration === null || integration === void 0 ? void 0 : integration.isLinked()) {
                oauthTab.close();
                return 'ready';
            }
            if (oauthTab.isClosed()) {
                return 'tab_closed';
            }
            await wait(POLLING_INTERVAL);
        }
        return 'timeout';
    }, [
        accountingType,
        fetchIntegrationsSummary
    ]);
    const handleConnect = useCallback(async ()=>{
        const redirectUrl = "".concat(window.location.origin, "/home");
        const linkUrl = await connect(redirectUrl);
        if (linkUrl == null) {
            return;
        }
        const oauthTab = openNewTab(linkUrl, {
            trackable: true
        });
        setIsWaiting(true);
        onConnectingChange === null || onConnectingChange === void 0 ? void 0 : onConnectingChange(true);
        let result;
        try {
            result = await pollForReadyIntegration(oauthTab);
        } finally{
            setIsWaiting(false);
            onConnectingChange === null || onConnectingChange === void 0 ? void 0 : onConnectingChange(false);
        }
        if (result === 'ready') {
            onConnectedChange === null || onConnectedChange === void 0 ? void 0 : onConnectedChange(true);
            onContinue();
        } else if (result === 'tab_closed' || result === 'timeout') {
            showErrorToast(t.connectionFailed(), 'integration-connection-failed-toast');
        }
    }, [
        connect,
        onConnectedChange,
        onContinue,
        onConnectingChange,
        openNewTab,
        pollForReadyIntegration,
        showErrorToast,
        t
    ]);
    const handleSubmit = useCallback(async ()=>{
        if (connected) {
            onContinue();
            return;
        }
        await handleConnect();
    }, [
        connected,
        handleConnect,
        onContinue
    ]);
    useImperativeHandle(submitRef, ()=>({
            submit: handleSubmit
        }), [
        handleSubmit
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.root,
        "data-testid": testIds.QboXeroIntegrationStepTestIds.Root(),
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: styles.lottieContainer,
                children: /*#__PURE__*/ _jsx(LottiePlayer, {
                    animationData: animationData,
                    loop: true,
                    autoplay: true,
                    className: styles.lottie,
                    testId: testIds.QboXeroIntegrationStepTestIds.LottieAnimation()
                })
            }),
            connected && /*#__PURE__*/ _jsx("div", {
                "data-testid": testIds.QboXeroIntegrationStepTestIds.ConnectedText(),
                children: /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraph2",
                    className: styles.connected,
                    children: connectedMessage
                })
            }),
            isWaiting && /*#__PURE__*/ _jsx("div", {
                "data-testid": testIds.QboXeroIntegrationStepTestIds.WaitingText(),
                children: /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraph2",
                    className: styles.waiting,
                    children: t.waiting()
                })
            })
        ]
    });
};
